import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Users, Lock, Globe, Mail, Trash2 } from 'lucide-react';
import { AuthContext } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import AdBanner from '../components/AdBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

export default function ServerDetail() {
  const { serverId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [server, setServer] = useState(null);
  const [posts, setPosts] = useState([]);
  const [isMember, setIsMember] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const config = {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true
        };

        const [serverRes, postsRes] = await Promise.all([
          axios.get(`${API}/api/servers/${serverId}`, config),
          axios.get(`${API}/api/posts?server_id=${serverId}`, config)
        ]);

        setServer(serverRes.data);
        setPosts(postsRes.data);

        if (user) {
          const memberRes = await axios.get(`${API}/api/servers`, config);
          const userServers = memberRes.data.filter(s => s.server_id === serverId);
          setIsMember(userServers.length > 0);
        }
      } catch (error) {
        console.error('Error fetching server data:', error);
        if (error.response?.status === 403) {
          toast.error('Access denied to this private server');
          navigate('/home');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [serverId, user, navigate]);

  const handleJoinServer = async () => {
    if (!user) {
      navigate('/auth');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/servers/${serverId}/join`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });

      toast.success('Joined server successfully!');
      setIsMember(true);
      window.location.reload();
    } catch (error) {
      console.error('Error joining server:', error);
      toast.error(error.response?.data?.detail || 'Failed to join server');
    }
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/servers/${serverId}/invite`, {
        invitee_email: inviteEmail
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });

      toast.success('Invite sent!');
      setShowInviteDialog(false);
      setInviteEmail('');
    } catch (error) {
      console.error('Error sending invite:', error);
      toast.error('Failed to send invite');
    }
  };

  const handleDeleteServer = async () => {
    if (!window.confirm('Are you sure you want to delete this server? All posts will be deleted.')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/servers/${serverId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });

      toast.success('Server deleted');
      navigate('/home');
    } catch (error) {
      console.error('Error deleting server:', error);
      toast.error('Failed to delete server');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (!server) return null;

  const isOwner = user && server.creator_id === user.user_id;

  return (
    <div className="min-h-screen bg-white">
      <nav className="border-b-4 border-black bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center">
              <Button
                data-testid="back-button"
                onClick={() => navigate('/home')}
                variant="ghost"
                className="mr-4 border-2 border-black rounded-full"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <h1 className="text-2xl font-black text-slate-900" style={{fontFamily: 'Outfit'}}>{server.name}</h1>
              {server.is_private ? (
                <Lock className="w-5 h-5 text-pink-500 ml-2" />
              ) : (
                <Globe className="w-5 h-5 text-lime-500 ml-2" />
              )}
            </div>
            {isOwner && (
              <Button
                data-testid="delete-server-button"
                onClick={handleDeleteServer}
                variant="ghost"
                className="text-red-500 hover:text-red-600 border-2 border-black rounded-full"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Server
              </Button>
            )}
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8 bg-white border-4 border-black rounded-2xl p-6 shadow-brutal">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-slate-700 mb-4">{server.description}</p>
              <div className="flex items-center gap-4 text-sm text-slate-600">
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  {server.member_count} members
                </span>
                <span>•</span>
                <span>{server.category}</span>
              </div>
            </div>
            <div className="flex gap-2">
              {!isMember && !server.is_private && (
                <Button
                  data-testid="join-server-button"
                  onClick={handleJoinServer}
                  className="bg-lime-400 hover:bg-lime-500 text-slate-900 border-2 border-black rounded-full font-bold shadow-brutal"
                >
                  Join Server
                </Button>
              )}
              {isMember && server.is_private && (
                <Button
                  data-testid="invite-button"
                  onClick={() => setShowInviteDialog(true)}
                  variant="outline"
                  className="border-2 border-black rounded-full font-bold"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Invite
                </Button>
              )}
            </div>
          </div>
        </div>

        {isMember && (
          <div className="mb-8 flex justify-between items-center">
            <h2 className="text-3xl font-black text-slate-900" style={{fontFamily: 'Outfit'}}>Posts</h2>
            <Button
              data-testid="create-moment-button"
              onClick={() => navigate('/create-post', { state: { serverId } })}
              className="bg-indigo-500 hover:bg-indigo-600 text-white border-2 border-black rounded-full font-bold shadow-brutal"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add a moment
            </Button>
          </div>
        )}

        {!isMember ? (
          <div className="text-center py-16 border-4 border-black rounded-3xl bg-slate-50">
            <div className="w-24 h-24 bg-indigo-100 rounded-full mx-auto mb-6 flex items-center justify-center border-2 border-black">
              <Lock className="w-12 h-12 text-indigo-500" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Outfit'}}>Join to see moments</h3>
            <p className="text-slate-600 mb-6">{server.is_private ? 'This is a private server. You need an invite to join.' : 'Join this server to view and add moments'}</p>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16 border-4 border-dashed border-slate-300 rounded-3xl">
            <h3 className="text-2xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Outfit'}}>Your kind is out there.</h3>
            <p className="text-slate-600 mb-6">Be the first to add a moment!</p>
            <Button
              onClick={() => navigate('/create-post', { state: { serverId } })}
              className="bg-indigo-500 hover:bg-indigo-600 text-white border-2 border-black rounded-full font-bold shadow-brutal"
            >
              Add a moment
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            <AdBanner />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((post, index) => (
                <React.Fragment key={post.post_id}>
                  <div
                    data-testid={`post-card-${post.post_id}`}
                    onClick={() => navigate(`/post/${post.post_id}`)}
                    className="bg-white border-4 border-black rounded-2xl p-6 shadow-brutal cursor-pointer hover:shadow-brutal-lg transition-shadow"
                  >
                    <h3 className="text-xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Outfit'}}>{post.title}</h3>
                    <p className="text-slate-600 text-sm mb-4 line-clamp-2">{post.description}</p>
                    <p className="text-xs text-slate-500">by {post.user_name}</p>
                  </div>
                  {(index + 1) % 6 === 0 && <div className="md:col-span-2 lg:col-span-3"><AdBanner /></div>}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </div>

      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="border-4 border-black rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black" style={{fontFamily: 'Outfit'}}>Invite to {server.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSendInvite} className="space-y-4 pt-4">
            <div>
              <Label htmlFor="invite-email" className="font-bold text-slate-900">Email Address</Label>
              <Input
                id="invite-email"
                data-testid="invite-email-input"
                type="email"
                required
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="friend@example.com"
                className="mt-1 h-12 border-2 border-black rounded-xl"
              />
            </div>
            <Button
              data-testid="send-invite-button"
              type="submit"
              className="w-full bg-indigo-500 hover:bg-indigo-600 text-white border-2 border-black rounded-full font-bold shadow-brutal"
            >
              Send Invite
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}